// Find anything by name (PLAN-2026-10-17 §6, B-402): the kit's half. A box to
// type in and the matches under it in groups — players, the league's screens,
// its teams, my leagues — from anywhere, by a key or the top bar's search.
// Mantine's Spotlight underneath, which no screen imports (LOOKS.md Part 7):
// the shell says what can be found and what each does.
//
// THE KIT DOES NOT FILTER. What matches a query is the shell's business (a
// player is found by the server, a screen by its name), so the groups arrive
// already narrowed and are drawn in the order given.

import type { ReactNode } from 'react';
import { Spotlight, createSpotlight, useSpotlight } from '@mantine/spotlight';
import '@mantine/spotlight/styles.css';
import { BackCloses } from './backCloses';
import { Icon, type IconName } from './Icon';
import { isPhoneWidth, useViewportWidth } from './viewport';

const [store, finder] = createSpotlight();

/** Open the finder from anywhere: the top bar's search, a menu. */
export function openFinder(): void {
  finder.open();
}

export function closeFinder(): void {
  finder.close();
}

/** Whether the finder is open: what it finds is asked for only then. */
export function useFinderOpen(): boolean {
  return useSpotlight(store).opened;
}

export interface FinderItem {
  id: string;
  label: string;
  /** A second line: a player's club and who has him, a screen's place. */
  sub?: string;
  icon?: IconName;
  /** Drawn in place of the icon: a position, a team's mark. */
  lead?: ReactNode;
  trail?: ReactNode;
  onPick: () => void;
}

export interface FinderGroup {
  label: string;
  items: FinderItem[];
}

export function Finder({
  groups,
  query,
  onQuery,
  placeholder,
  empty,
  note,
  zIndex,
}: {
  groups: FinderGroup[];
  query: string;
  onQuery: (query: string) => void;
  placeholder: string;
  /** Said when every group is empty. */
  empty: ReactNode;
  /** A line at the foot: a search in flight, a failure. */
  note?: ReactNode;
  zIndex?: number;
}) {
  const { opened } = useSpotlight(store);
  // A PHONE'S FINDER IS THE WHOLE SCREEN, as a phone's search is, with the
  // keyboard up under it.
  const phone = useViewportWidth(isPhoneWidth);
  const shown = groups.filter((g) => g.items.length > 0);
  return (
    <>
      {/* Back closes it before it leaves the screen (UX-13, B-308). */}
      <BackCloses opened={opened} onClose={closeFinder} />
      <Spotlight.Root
        store={store}
        query={query}
        onQueryChange={onQuery}
        // `/` as on most sites with a search, and Ctrl+K or ⌘K as on most
        // apps with a finder; never while something is being typed in.
        shortcut={['mod + K', '/']}
        // As tall as what it finds; a long list scrolls the finder itself.
        fullScreen={phone}
        zIndex={zIndex}
        classNames={{ content: 'fsp-finder', action: 'fsp-finder-action' }}
      >
        <Spotlight.Search
          placeholder={placeholder}
          aria-label={placeholder}
          leftSection={<Icon name="search" />}
        />
        <Spotlight.ActionsList aria-label="Matches">
          {shown.map((g) => (
            <Spotlight.ActionsGroup key={g.label} label={g.label}>
              {g.items.map((item) => (
                <Spotlight.Action
                  key={item.id}
                  label={item.label}
                  description={item.sub}
                  leftSection={item.lead ?? (item.icon ? <Icon name={item.icon} /> : undefined)}
                  rightSection={item.trail}
                  onClick={item.onPick}
                />
              ))}
            </Spotlight.ActionsGroup>
          ))}
          {shown.length === 0 ? <Spotlight.Empty>{empty}</Spotlight.Empty> : null}
        </Spotlight.ActionsList>
        {note ? <Spotlight.Footer>{note}</Spotlight.Footer> : null}
      </Spotlight.Root>
    </>
  );
}
